import { create } from 'zustand';
import {
  getTree,
  searchUsers,
  reassignManager,
  HierarchyNode,
  TreeResponse,
  SearchHit,
} from '@/services/PagesServices/OrgHierarchyService';

type ActionResult = { success: boolean; message: string };

interface OrgHierarchyState {
  tree: HierarchyNode[];
  totalUsers: number;
  rootCount: number;

  searchResults: SearchHit[];
  loadingTree: boolean;
  searching: boolean;
  saving: boolean;
  error: string | null;

  fetchTree: () => Promise<void>;
  search: (q: string) => Promise<void>;
  clearSearch: () => void;
  reassignManager: (uuid: string, managerUuid: string | null) => Promise<ActionResult>;
}

const useOrgHierarchyStore = create<OrgHierarchyState>((set, get) => ({
  tree: [],
  totalUsers: 0,
  rootCount: 0,
  searchResults: [],
  loadingTree: false,
  searching: false,
  saving: false,
  error: null,

  fetchTree: async () => {
    set({ loadingTree: true, error: null });
    const res = await getTree();
    if (res.success && res.data) {
      const data: TreeResponse = res.data;
      set({ tree: data.tree, totalUsers: data.totalUsers, rootCount: data.rootCount, loadingTree: false });
    } else {
      set({ error: res.error?.message ?? 'Failed to load hierarchy', loadingTree: false });
    }
  },

  search: async (q) => {
    if (!q.trim()) { set({ searchResults: [] }); return; }
    set({ searching: true });
    const res = await searchUsers(q);
    set({ searchResults: res.success ? (res.data || []) : [], searching: false });
  },

  clearSearch: () => set({ searchResults: [] }),

  reassignManager: async (uuid, managerUuid) => {
    set({ saving: true });
    const res = await reassignManager(uuid, managerUuid);
    set({ saving: false });
    if (res.success) {
      await get().fetchTree();
      return { success: true, message: res.message ?? 'Manager updated' };
    }
    return { success: false, message: res.error?.message ?? 'Failed to update manager' };
  },
}));

export default useOrgHierarchyStore;
